import React, { useEffect } from 'react';
import { create } from 'zustand';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export type ToastKind = 'info' | 'success' | 'warning' | 'error';

interface ToastItem { id: number; kind: ToastKind; message: React.ReactNode; durationMs: number }

interface ToastState {
  toasts: ToastItem[];
  push: (kind: ToastKind, message: React.ReactNode, durationMs?: number) => void;
  dismiss: (id: number) => void;
}

let nextId = 1;

export const useToastStore = create<ToastState>((set) => ({
  toasts: [],
  push: (kind, message, durationMs = kind === 'error' ? 8000 : 4000) =>
    set(s => ({ toasts: [...s.toasts.slice(-4), { id: nextId++, kind, message, durationMs }] })),
  dismiss: (id) => set(s => ({ toasts: s.toasts.filter(t => t.id !== id) })),
}));

/** `toast.success('Saved')` from anywhere — replaces the per-module toast
 *  implementations and the native alert() calls. */
export const toast = {
  info: (m: React.ReactNode) => useToastStore.getState().push('info', m),
  success: (m: React.ReactNode) => useToastStore.getState().push('success', m),
  warning: (m: React.ReactNode) => useToastStore.getState().push('warning', m),
  error: (m: React.ReactNode) => useToastStore.getState().push('error', m),
};

const ToastView: React.FC<{ item: ToastItem }> = ({ item }) => {
  const { t } = useTranslation();
  const dismiss = useToastStore(s => s.dismiss);
  useEffect(() => {
    const timer = setTimeout(() => dismiss(item.id), item.durationMs);
    return () => clearTimeout(timer);
  }, [item.id, item.durationMs, dismiss]);
  return (
    <div className={`ds-toast ds-toast--${item.kind}`} role={item.kind === 'error' ? 'alert' : 'status'}>
      <div className="ds-toast__body">{item.message}</div>
      <button type="button" className="ds-toast__close" onClick={() => dismiss(item.id)} aria-label={t('common.dismiss', 'Dismiss')}><X size={14} /></button>
    </div>
  );
};

/** Mounted once at the root of App. */
export const ToastHost: React.FC = () => {
  const toasts = useToastStore(s => s.toasts);
  return <div className="ds-toasts">{toasts.map(t => <ToastView key={t.id} item={t} />)}</div>;
};

export default ToastHost;
